import * as React from "react";
import { useCurrentProject } from "@/lib/project";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, MoreHorizontal, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/bespoke";
import { formatElapsed } from "@/lib/format";

export type EvalRun = {
  id: string;
  projectId: string;
  datasetId: string;
  graderId: string;
  name: string;
  environment: string;
  trigger: string;
  status: "Running" | "Completed" | "Failed" | "Cancelled";
  totalItems: number;
  completedItems: number;
  startedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

async function fetchRuns(projectId: string): Promise<EvalRun[]> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs`, {
    method: "GET",
    credentials: "same-origin",
  });
  if (!response.ok) return [];
  return (await response.json()).runs;
}

async function createRun(
  projectId: string,
  input: {
    datasetId: string;
    graderId: string;
    name: string;
    environment: string;
    trigger: string;
  },
): Promise<EvalRun> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return response.json();
}

async function completeRun(
  projectId: string,
  runId: string,
  status: "Completed" | "Failed" | "Cancelled",
): Promise<EvalRun> {
  const response = await fetch(`/api/v1/projects/${projectId}/eval/runs/${runId}:complete`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  return response.json();
}

async function deleteRun(projectId: string, runId: string): Promise<void> {
  await fetch(`/api/v1/projects/${projectId}/eval/runs/${runId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
}

function getStatusBadge(status: EvalRun["status"]) {
  const variants: Record<EvalRun["status"], "info" | "success" | "destructive" | "outline"> = {
    Running: "info",
    Completed: "success",
    Failed: "destructive",
    Cancelled: "outline",
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}

function RunRow({
  run,
  onComplete,
  onDelete,
}: {
  run: EvalRun;
  onComplete: (status: "Completed" | "Failed" | "Cancelled") => void;
  onDelete: () => void;
}) {
  const progress = run.totalItems > 0 ? Math.round((run.completedItems / run.totalItems) * 100) : 0;

  return (
    <tr className="group hover:bg-muted/50">
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-medium">
        {run.name}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        {run.environment}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground max-w-xs truncate">
        {run.trigger}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        {getStatusBadge(run.status)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <div className="flex items-center gap-2">
          <div className="flex-1 max-w-[150px] h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-sm text-muted-foreground tabular-nums w-[50px] text-right">
            {run.completedItems}/{run.totalItems}
          </span>
        </div>
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 text-muted-foreground">
        {formatElapsed(run.startedAt)}
      </td>
      <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {run.status === "Running" && (
              <>
                <DropdownMenuItem onClick={() => onComplete("Completed")}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Mark Completed
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onComplete("Failed")} className="text-destructive">
                  <RefreshCw className="mr-2 h-4 w-4" /> Mark Failed
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onComplete("Cancelled")}>
                  <RefreshCw className="mr-2 h-4 w-4" /> Cancel
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuItem onClick={onDelete} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

export function EvalRunsPage({ headingLevel = "h1" }: { headingLevel?: "h1" | "h2" }) {
  const { project } = useCurrentProject();
  const projectId = project?.id;
  const queryClient = useQueryClient();

  const { data: runs, isLoading } = useQuery({
    queryKey: ["eval-runs", projectId],
    queryFn: () => fetchRuns(projectId!),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (input: {
      datasetId: string;
      graderId: string;
      name: string;
      environment: string;
      trigger: string;
    }) => createRun(projectId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-runs", projectId] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: ({
      runId,
      status,
    }: {
      runId: string;
      status: "Completed" | "Failed" | "Cancelled";
    }) => completeRun(projectId!, runId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-runs", projectId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (runId: string) => deleteRun(projectId!, runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eval-runs", projectId] });
    },
  });

  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [filterStatus, setFilterStatus] = React.useState<string>("all");

  const filteredRuns =
    runs?.filter((r) => {
      const matchesSearch =
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.environment.toLowerCase().includes(search.toLowerCase()) ||
        r.trigger.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = filterStatus === "all" || r.status === filterStatus;
      return matchesSearch && matchesStatus;
    }) ?? [];

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createMutation.mutate({
      datasetId: formData.get("datasetId") as string,
      graderId: formData.get("graderId") as string,
      name: formData.get("name") as string,
      environment: formData.get("environment") as string,
      trigger: formData.get("trigger") as string,
    });
    setIsCreateOpen(false);
    e.currentTarget.reset();
  };

  const handleComplete = (runId: string, status: "Completed" | "Failed" | "Cancelled") => {
    completeMutation.mutate({ runId, status });
  };

  const handleDelete = (runId: string) => {
    if (confirm("Are you sure you want to delete this run?")) {
      deleteMutation.mutate(runId);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Eval Runs"
        headingLevel={headingLevel}
        description="Execute graders against datasets and track results."
        actions={
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New run
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search runs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="Running">Running</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Failed">Failed</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isCreateOpen && (
            <form
              onSubmit={handleCreate}
              className="mb-4 p-4 border rounded-lg bg-muted/30 space-y-3"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <Input name="name" placeholder="My eval run" required autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Environment</label>
                  <Input name="environment" placeholder="production" required />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Dataset</label>
                  <Select name="datasetId" required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select dataset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>
                        Select dataset...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Grader</label>
                  <Select name="graderId" required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select grader" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" disabled>
                        Select grader...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Trigger</label>
                <Input name="trigger" placeholder="Manual run" required />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create run"}
                </Button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Eval Runs</caption>
              <thead>
                <tr className="border-b">
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[20%]"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[12%]"
                  >
                    Environment
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[18%]"
                  >
                    Trigger
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[12%]"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[18%]"
                  >
                    Progress
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[12%]"
                  >
                    Started
                  </th>
                  <th
                    scope="col"
                    className="h-10 px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5 w-[8%]"
                  ></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-muted-foreground">
                      Loading runs...
                    </td>
                  </tr>
                ) : filteredRuns.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8">
                      <p className="font-medium">No eval runs yet</p>
                      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                        Create a run to execute a grader against a dataset.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredRuns.map((run) => (
                    <RunRow
                      key={run.id}
                      run={run}
                      onComplete={(status) => handleComplete(run.id, status)}
                      onDelete={() => handleDelete(run.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
